import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Bell,
  Plus,
  Edit,
  Trash2,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Mail,
  UserX
} from 'lucide-react';
import {
  getMarketingConsents,
  createMarketingConsent,
  updateMarketingConsent,
  optOutMarketingConsent,
  deleteMarketingConsent
} from '../../../redux/api';

const CHANNEL_LABELS = {
  registration: 'Registracija',
  viber: 'Viber',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  website: 'Sajt',
  manual: 'Ručno',
  other: 'Ostalo'
};

const CHANNEL_OPTIONS = Object.entries(CHANNEL_LABELS).map(([value, label]) => ({ value, label }));

const emptyForm = {
  email: '',
  ownerName: '',
  companyName: '',
  phone: '',
  consentChannel: 'manual',
  notes: ''
};

const MarketingConsentView = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const [showForm, setShowForm] = useState(false);
  const [editingConsent, setEditingConsent] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const fetchConsents = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = { active: filterActive };
      if (filterChannel) params.channel = filterChannel;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await getMarketingConsents(page, 20, params);
      setConsents(response.data.consents || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(response.data.page || page);
    } catch (error) {
      console.error(error);
      toast.error('Greška pri učitavanju liste saglasnosti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }
    fetchConsents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, filterChannel, filterActive]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConsents(1);
  };

  const openCreateForm = () => {
    setEditingConsent(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (consent) => {
    setEditingConsent(consent);
    setFormData({
      email: consent.email || '',
      ownerName: consent.ownerName || '',
      companyName: consent.companyName || '',
      phone: consent.phone || '',
      consentChannel: consent.consentChannel || 'manual',
      notes: consent.notes || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      toast.error('Email je obavezan');
      return;
    }

    setSubmitting(true);
    try {
      if (editingConsent) {
        await updateMarketingConsent({
          id: editingConsent._id,
          data: {
            ownerName: formData.ownerName,
            companyName: formData.companyName,
            phone: formData.phone,
            consentChannel: formData.consentChannel,
            notes: formData.notes
          }
        });
        toast.success('Saglasnost je ažurirana');
      } else {
        await createMarketingConsent(formData);
        toast.success('Saglasnost je dodata');
      }
      setShowForm(false);
      fetchConsents(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri čuvanju');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOptOut = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da odjavite ovog primaoca?')) return;
    try {
      await optOutMarketingConsent(id);
      toast.success('Primalac je odjavljen');
      fetchConsents(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri odjavi');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj zapis?')) return;
    try {
      await deleteMarketingConsent(id);
      toast.success('Zapis je obrisan');
      fetchConsents(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri brisanju');
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('sr-RS');
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6 text-white">
        <p>Nemate pristup ovoj stranici.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 text-white min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-green-400" />
            Marketing saglasnosti
          </h1>
          <p className="text-gray-400 mt-1">
            Svi koji su pristali da primaju obaveštenja — sa kanalom saglasnosti.
          </p>
        </div>
        <button
          onClick={openCreateForm}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          Dodaj saglasnost
        </button>
      </div>

      <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pretraga po emailu, imenu ili firmi..."
            className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
          />
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
          >
            <option value="">Svi kanali</option>
            {CHANNEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
          >
            <option value="true">Samo aktivni</option>
            <option value="false">Samo odjavljeni</option>
            <option value="">Svi</option>
          </select>
          <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg">
            Pretraži
          </button>
        </form>
      </div>

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-green-400" />
          </div>
        ) : consents.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Nema saglasnosti za prikaz.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 text-gray-300">
                <tr>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Ime / Firma</th>
                  <th className="text-left p-3">Telefon</th>
                  <th className="text-left p-3">Kanal</th>
                  <th className="text-left p-3">Datum saglasnosti</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {consents.map((consent) => (
                  <tr key={consent._id} className="border-t border-gray-700 hover:bg-gray-750">
                    <td className="p-3">{consent.email}</td>
                    <td className="p-3">
                      <div>{consent.ownerName || '—'}</div>
                      <div className="text-gray-400 text-xs">{consent.companyName || ''}</div>
                    </td>
                    <td className="p-3">{consent.phone || '—'}</td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded bg-gray-700 text-xs">
                        {CHANNEL_LABELS[consent.consentChannel] || consent.consentChannel}
                      </span>
                    </td>
                    <td className="p-3">{formatDate(consent.consentedAt)}</td>
                    <td className="p-3">
                      {consent.isActive ? (
                        <span className="text-green-400">Aktivan</span>
                      ) : (
                        <span className="text-red-400">
                          Odjavljen {consent.optOutAt ? `(${formatDate(consent.optOutAt)})` : ''}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditForm(consent)}
                          className="p-1.5 text-blue-400 hover:bg-gray-700 rounded"
                          title="Izmeni"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {consent.isActive && (
                          <button
                            onClick={() => handleOptOut(consent._id)}
                            className="p-1.5 text-yellow-400 hover:bg-gray-700 rounded"
                            title="Odjavi"
                          >
                            <UserX className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(consent._id)}
                          className="p-1.5 text-red-400 hover:bg-gray-700 rounded"
                          title="Obriši"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
          <span>Ukupno: {total}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchConsents(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-2 rounded bg-gray-800 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span>Strana {currentPage} / {totalPages}</span>
            <button
              onClick={() => fetchConsents(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-2 rounded bg-gray-800 disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-lg border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold">
                {editingConsent ? 'Izmeni saglasnost' : 'Dodaj saglasnost'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  disabled={!!editingConsent}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white disabled:opacity-60"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Ime</label>
                  <input
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Firma</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Telefon</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Kanal saglasnosti *</label>
                <select
                  value={formData.consentChannel}
                  onChange={(e) => setFormData({ ...formData, consentChannel: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                >
                  {CHANNEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Napomena</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  placeholder="npr. Pristao na Viberu 12.06.2026."
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Otkaži
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-60"
                >
                  {submitting ? 'Čuvanje...' : 'Sačuvaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingConsentView;
